import { getObjectLabels } from '@paintscout/util/builder'
import { useClientOptions } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import type { QuoteDocument } from 'paintscout'
import { useCallback, useContext, useRef, useState } from 'react'
import { QuoteContext } from '../context/QuoteContext'
import { fetchFreshQuote } from './freshQuoteState'
import { findNewPaymentActivity } from './newPaymentActivity'

export interface CheckForNewPaymentsResult {
  /** True when the flow that asked should abort — the user needs to review remote payment activity first. */
  blocked: boolean
  /** The doc the caller should act on: the fresh one when it's safe to adopt, otherwise the local one. */
  quote: QuoteDocument
}

/**
 * Guards a payment-recording flow against the duplicate-payment scenario in #21913: someone records
 * a payment in customer view while an estimator has the same quote open, and the estimator records
 * it again on top of their stale copy (the server's `shouldReject` doesn't reject saves on payment
 * changes, so it goes through silently).
 *
 * On check we refetch, diff the payments, and — if the server has activity we've never seen — warn,
 * refresh the table when we safely can, and tell the caller to abort. Fails open in every direction:
 * with no `refetchQuote` (customer view, unsaved quotes) or a failed fetch, the flow proceeds.
 */
export function useCheckForNewPayments() {
  const { quote, isDirty, updateQuote, refetchQuote } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const [checking, setChecking] = useState(false)
  // Ref rather than the state flag so a double-click can't slip a second refetch through before the
  // re-render lands.
  const checkingRef = useRef(false)

  const check = useCallback(async (): Promise<CheckForNewPaymentsResult> => {
    if (checkingRef.current) {
      return { blocked: true, quote }
    }
    checkingRef.current = true
    setChecking(true)

    try {
      const fresh = await fetchFreshQuote(refetchQuote)
      // hasNewActivity implies a fresh doc — findNewPaymentActivity reports nothing without one, which
      // is what makes the whole guard fail open on a missing refetch or a failed fetch.
      const { hasNewActivity } = findNewPaymentActivity({ current: quote, fresh })

      if (!hasNewActivity) {
        // Adopt the fresh doc when we can, so the dialog doesn't write back a stale _rev.
        return { blocked: false, quote: !isDirty && fresh ? fresh : quote }
      }

      const objectLabel = getObjectLabels({ options, invoice: quote?.is_invoice }).quote.value.toLowerCase()

      if (isDirty) {
        // Never overwrite local state while dirty — it would drop the estimator's unsaved edits.
        enqueueSnackbar(
          `This ${objectLabel} was updated with new payment activity elsewhere. Save or discard your changes and review the payments before adding one.`,
          { variant: 'warning' }
        )
      } else {
        // Push the fresh doc in explicitly: currentQuote is `dirtyQuote || cleanQuote`, so a previous
        // blockDirty update leaves a non-null dirtyQuote that would mask the refreshed clean doc.
        updateQuote?.({ quote: fresh, blockDirty: true })
        enqueueSnackbar(
          `This ${objectLabel} was updated with new payment activity. We've refreshed it — please review before adding a payment.`,
          { variant: 'warning' }
        )
      }

      // The fresh doc when we adopted it above, the local one when dirty — so the returned quote
      // always matches the state the table is now showing, blocked or not.
      return { blocked: true, quote: !isDirty && fresh ? fresh : quote }
    } catch (e) {
      // Fail open: this guard exists to prevent a duplicate payment, never to stand between the user
      // and a legitimate one. fetchFreshQuote already swallows fetch errors, so anything landing here
      // is unexpected and worth reporting.
      Sentry.captureException(e, { tags: { quoteId: quote?._id } })
      return { blocked: false, quote }
    } finally {
      checkingRef.current = false
      setChecking(false)
    }
  }, [quote, isDirty, updateQuote, refetchQuote, options, enqueueSnackbar])

  return { checking, check }
}
